import { AttendanceCalendar } from './AttendanceCalendar';
import React, { useEffect, useState } from 'react';
import { CheckIn } from './CheckIn';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import { AttendanceHistory } from './AttendanceHistory';
import { OpenRequests } from './OpenRequests';
import { ApplyLeaveComponent } from './ApplyLeaveComponent';
import { useAttendanceData } from './hooks/useAttendanceData';
import { getAttendancePage, getEmployeeDetailsLink, getPeoplePage } from 'utils/links';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { Redirect } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { EEmployeeType } from 'enums';
import { useTodaysAttendanceData } from './hooks/useTodaysAttendanceData';
import { AttendanceSidebar } from './AttendanceSidebar';
import useFeature from 'hooks/useFeature';
import userRoles from 'constants/userRoles';

const Attendance = () => {
  const todaysData = useTodaysAttendanceData();
  const attendanceData = useAttendanceData({ enabled: todaysData.isSuccess });
  const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
  const currentEmployee = currentOrganization?.employeeDetails;
  const { isEmployeeOnboardingEnabled } = useFeature('employeeOnboarding');
  const { ubitechAttendanceDashboardUrl } = useReduxSelector(loggedInUserSelectors.referenceLinks);
  const currentUserRole = useReduxSelector(loggedInUserSelectors.role);
  const isNonEmployee = currentUserRole !== userRoles.EMPLOYEE;
  const [showUbitechModal, setShowUbitechModal] = useState(false);

  useEffect(() => {
    if (currentOrganization?.shouldRedirectAttendanceToUbitech && ubitechAttendanceDashboardUrl) {
      if (isNonEmployee) {
        setShowUbitechModal(true);
      } else {
        window.open(ubitechAttendanceDashboardUrl as string, '_blank');
      }
    }
  }, [currentOrganization?.shouldRedirectAttendanceToUbitech, ubitechAttendanceDashboardUrl, isNonEmployee]);

  const handleContinueToUbitech = () => {
    window.open(ubitechAttendanceDashboardUrl as string, '_blank');
    setShowUbitechModal(false);
  };

  if (todaysData.status === 'loading') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%">
        <Spinner size="medium" accessibilityLabel="Fetching attendance data" />
      </Box>
    );
  }

  const shouldRedirectToUbitech =
    !!currentOrganization?.shouldRedirectAttendanceToUbitech && !!ubitechAttendanceDashboardUrl;

  if (
    !shouldRedirectToUbitech &&
    (!currentOrganization?.isAttendance ||
      (currentEmployee?.employeeType === EEmployeeType.CONTRACTOR &&
        !currentOrganization.leaveAttendanceConfig?.enableContractorsAttendance &&
        attendanceData.isForLoggedInUser) ||
      todaysData.error?.getDetails().type === 403 ||
      todaysData.error?.getDetails().type === 404 ||
      attendanceData.error?.getDetails().type === 403 ||
      attendanceData.error?.getDetails().type === 404)
  ) {
    return <Redirect to={routePaths.accessDenied} />;
  }

  return (
    <Box display={'flex'} flexDirection={{ base: 'column', l: 'row' }}>
      <Box
        display="flex"
        flexDirection="column"
        gap="spacing.8"
        padding="spacing.8"
        paddingTop="spacing.8"
        maxWidth={{ l: 'calc(100% - 267px)' }}
        flexGrow={1}>
        {!attendanceData.isForLoggedInUser && (
          <Breadcrumb>
            <BreadcrumbItem href={getPeoplePage(isEmployeeOnboardingEnabled)}>People</BreadcrumbItem>
            <BreadcrumbItem
              href={getEmployeeDetailsLink(
                attendanceData.userId ? attendanceData.userId : undefined,
              )}>
              {(todaysData.data && todaysData.data.employee_name) || '...'}
            </BreadcrumbItem>
            <BreadcrumbItem
              href={getAttendancePage(attendanceData.userId ? attendanceData.userId : undefined)}
              isCurrentPage>
              Leave and attendance
            </BreadcrumbItem>
          </Breadcrumb>
        )}
        <CheckIn />
        <AttendanceCalendar />
        <ApplyLeaveComponent />
        <OpenRequests />
        <AttendanceHistory />
      </Box>
      <AttendanceSidebar />
      <Modal isOpen={showUbitechModal} onDismiss={() => setShowUbitechModal(false)} size="small">
        <ModalHeader title="Attendance is managed on Ubitech" />
        <ModalBody>
          <Text>
            Your organization uses Ubitech as the preferred tool for attendance management as per
            org settings. Clicking Continue will open Ubitech in a new tab.
          </Text>
          <Box marginTop="spacing.4">
            <Link href="/integration/partner?partner-id=201" target="_blank">
              Disable Ubitech integration
            </Link>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.3" justifyContent="flex-end">
            <Button variant="secondary" onClick={() => setShowUbitechModal(false)}>
              Stay here
            </Button>
            <Button onClick={handleContinueToUbitech}>Continue to Ubitech</Button>
          </Box>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export default Attendance;
